import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {URLHelper} from 'helpers/url.helper';
import {DataService} from '../data-service';
import {DataListHelper} from 'helpers/data-list.helper';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Observable} from 'rxjs/Observable';
import {
  Card, DiscountAppliedFrom, DiscountType,
  ParkingInventory,
  ParkingInventoryStatus, ParkingInventoryType,
  ParkingLotCard,
  ParkingLotLift, ParkingLotMemberQuery,
  ParkingLotUsage,
  ResponseDelete,
  ResponseList,
  Subscriber, SubscriberVirtualCard
} from 'helpers/interfaces';
import {catchError, map} from 'rxjs/operators';
import {CardsStoreActions} from 'pages/manage-parking/manage-member/services/card.service';

@Injectable()
export class ParkingDataService extends DataService {

  deleteAccessibility(accessibilityId: Array<any>): Promise<any> {
    const accessibilityIds = accessibilityId.map((data) => {
      return data.id;
    }).join(',');
    return this.delete('', {'parking-lots': 'PARKING_LOT_ID'}, '/accessibility?ids=' + accessibilityIds);
  }

  updateMemberRoleMember(memberId: number, roleMemberBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json'), params: {}};
    if (roleMemberBody.id) {
      options.params = {roleMemberId: roleMemberBody.id};
    }
    return this.put('', {'parking-lots': 'PARKING_LOT_ID', 'members': memberId}, '/role-members', '', options);
  }

  updateMemberGroupSpace(memberId: number, groupSpaceBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json'), params: {}};
    if (groupSpaceBody.id) {
      options.params = {parkingLotGroupSpaceId: groupSpaceBody.id};
    }
    return this.put('', {
      'parking-lots': 'PARKING_LOT_ID',
      'members': memberId
    }, '/parking-lot-group-spaces', '', options);
  }

  createAccessibility(accessibilityBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.post('', {'parking-lots': 'PARKING_LOT_ID'}, '/accessibility', accessibilityBody, options);
  }

  getAccessibility(options: any, searchFilter: string, searchCategory?: any): Promise<any> {
    const url = URLHelper.buildQueryString('/role-members', '', '');
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, url + '');
  }

  getRoleMembers(): Promise<any> {
    const url = URLHelper.buildQueryString('/role-members', '', '');
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, url + '');
  }

  changePassword(password): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.post('users/me/change-password', '', '', password, options);
  }

  getParkingLotById(): Promise<any> {
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, '');
  }

  getParkingLotSpaces(options: any, searchFilter: string, searchCategory?: any): Promise<any> {
    options = options || {};
    options.query = options.query || {};
    options.query['query'] = URLHelper.buildGeneralSearchFilter(
      {'name': 'string'},
      searchFilter || '*');
    const url = URLHelper.buildQueryString('/spaces/search?', options.query, '');
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, url + '&sort=createdDate,desc');
  }

  getGroupSpaces(options: any, searchFilter: string, searchCategory?: any): Promise<any> {
    options = options || {};
    options.query = options.query || {};
    options.query['query'] = URLHelper.buildGeneralSearchFilter(
      {'name': 'string'},
      searchFilter || '*');
    let url = URLHelper.buildQueryString('/group-spaces?', options.query, '');
    if (searchCategory) {
      url = url + `;(enable==${searchCategory})`;
    }
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, url + '&sort=createdDate,desc');
  }

  updateGroupSpace(id: number, groupSpaceBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.put('', {'parking-lots': 'PARKING_LOT_ID', 'group-spaces': id}, '', groupSpaceBody, options);
  }

  createGroupSpace(groupSpaceBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.post('', {'parking-lots': 'PARKING_LOT_ID'}, '/group-spaces', groupSpaceBody, options);
  }

  deleteGroupSpace(groupId: Array<any>): Promise<any> {
    const groupIdsFormat = groupId.map((data) => {
      return data.id;
    }).join(',');
    return this.delete('', {'parking-lots': 'PARKING_LOT_ID'}, '/group-spaces?ids=' + groupIdsFormat);
  }

  getParkingLifts(options: any, searchFilter: string, searchCategory?: any): Promise<ResponseList<ParkingLotLift>> {
    options = options || {};
    options.query = options.query || {};
    options.query['query'] = URLHelper.buildGeneralSearchFilter(
      {'name': 'string'},
      searchFilter || '*');
    let url = URLHelper.buildQueryString('/lifts/search?', options.query, '');
    if (searchCategory) {
      url = url + `;(enable==${searchCategory})`;
    }
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, url + '&sort=createdDate,desc');
  }

  getParkingLiftById(liftId): Promise<any> {
    const url = URLHelper.buildQueryString('/lifts/' + liftId, '', '');
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, url);
  }

  createParkingLift(liftBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.post('', {'parking-lots': 'PARKING_LOT_ID'}, '/lifts', liftBody, options);
  }

  updateParkingLift(liftId: number, liftBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.put('', {'parking-lots': 'PARKING_LOT_ID', 'lifts': liftId}, '', liftBody, options);
  }

  deleteParkingLifts(liftId: Array<any>): Promise<any> {
    const liftIdsFormat = liftId.map((data) => {
      return data.id;
    }).join(',');
    return this.delete('', {'parking-lots': 'PARKING_LOT_ID'}, '/lifts?ids=' + liftIdsFormat);
  }

  allParkingCards(): Promise<ResponseList<Card>> {
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, '/cards/search?' + 'offset=0&limit=10&query=(with=all=1)&sort=createdDate,desc');
  }

  createSubscriber(subscriber: Subscriber): Promise<Subscriber> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.post('', {'parking-lots': 'PARKING_LOT_ID'}, '/subscribers', subscriber, options);
  }

  updateSubscriber(subscriber: Subscriber): Promise<Subscriber> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.put('', {'parking-lots': 'PARKING_LOT_ID', 'subscribers': subscriber.id}, '', subscriber, options);
  }

  deleteSubscriber(ids: number[]): Promise<ResponseDelete> {
    return this.delete('', {'parking-lots': 'PARKING_LOT_ID'}, `/subscribers?ids=${ids.toString()}`);
  }

  addMemberCard(memberId: number, card: Card): Promise<Card> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.post('', {'parking-lots': 'PARKING_LOT_ID', 'members': memberId, 'cards': card.id}, '', card, options);
  }

  updateMemberCard(memberId: number, card: Card): Promise<Card> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.put('', {'parking-lots': 'PARKING_LOT_ID', 'members': memberId, 'cards': card.id}, '', card, options);
  }

  deleteMemberCard(memberId: number, ids: number[]): Promise<ResponseDelete> {
    return this.delete('', {'parking-lots': 'PARKING_LOT_ID', 'members': memberId}, `/cards?ids=${ids.toString()}`);
  }

  requestMemberExcel(memberIds: any, searchFilter: any): Promise<any> {
    const header = new HttpHeaders();
    header.append('Content-Type', 'application/json; charset=utf-8');
    header.append('mimeType', 'application/vnd.ms-excel');
    const options = {headers: header, responseType: 'blob', observe: 'response'};
    let url = URLHelper.buildQueryString('/members/export-excel?', '', '');
    if (memberIds) {
      url = url + '&query=id=' + ((memberIds.length == 0) ? 'out=(-1)' : 'in=(' + memberIds.join(',') + ')');
    } else {
      url = url + '&offset=0&limit=10000';
    }
    return this.post('reports/', {'parking-lots': 'PARKING_LOT_ID'}, url + '&sort=createdDate,desc', '', options);
  }

  requestSubscriberExcel(subscribersId: any, searchFilter: string): Promise<any> {
    const header = new HttpHeaders();
    header.append('Content-Type', 'application/json; charset=utf-8');
    header.append('mimeType', 'application/vnd.ms-excel');

    const options = {headers: header, responseType: 'blob', observe: 'response'};
    return this.post('', {'parking-lots': 'PARKING_LOT_ID'}, '/subscribers/export-excel?limit=' + (subscribersId.length || DataListHelper.MAXIMUN_QUERYABLE_ROW_COUNT) +
      '&query=id=' + ((subscribersId.length == 0) ? 'out=(-1)' : 'in=(' + subscribersId.join(',') + ')'), '', options);
  }

  importSubscriberExcel(fileInput): Promise<any> {
    if (fileInput.files.length == 0) {
      return new Promise((resolve, reject) => {
        reject(null);
      });
    }

    const formData: FormData = new FormData();
    formData.append('file', fileInput.files[0], fileInput.files[0].name);

    const header = new HttpHeaders();
    header.append('Content-Type', 'multipart/form-data');
    header.append('mimeType', 'multipart/form-data');

    const options = {headers: header};
    return this.post('', {'parking-lots': 'PARKING_LOT_ID'}, '/subscribers/import-excel', formData, options);
  }

  importMemberExcel(fileInput): Promise<any> {
    if (fileInput.files.length == 0) {
      return new Promise((resolve, reject) => {
        reject(null);
      });
    }

    const formData: FormData = new FormData();
    formData.append('file', fileInput.files[0], fileInput.files[0].name);

    const header = new HttpHeaders();
    header.append('Content-Type', 'multipart/form-data');
    header.append('mimeType', 'multipart/form-data');

    const options = {headers: header};
    return this.post('', {'parking-lots': 'PARKING_LOT_ID'}, '/members/import-excel', formData, options);
  }

  getParkingMemberById(memberId): Promise<any> {
    const url = URLHelper.buildQueryString('/members/' + memberId, '', '');
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, url);
  }

  getParkingMembers(options: any, searchFilter: string, searchDelete: boolean, searchCategory?: any, searchRoleMember?: any,): Promise<any> {
    const searchFilterTrim = searchFilter.trim();

    // Check license plate pattern
    const licensePlatePattern = /^(\d?[ก-๙]{1,2})[-\s]?\d{4}(?:\s[ก-๙\s]+)?$/;
    const isLicensePlatePattern = licensePlatePattern.test(searchFilterTrim);

    // Set default options
    options = options || {};
    options.query = options.query || {};

    if (isLicensePlatePattern) {
      const licensePlateInfo = this.extractThaiLicensePlateInfo(searchFilterTrim);
      options.query['licensePlateLetter'] = licensePlateInfo.licensePlateLetter;
      options.query['licensePlateNumber'] = licensePlateInfo.licensePlateNumber;
      options.query['licensePlateProvince'] = licensePlateInfo.licensePlateProvince;
    } else {
      options.query['query'] = URLHelper.buildGeneralSearchFilter(
        {'name': 'string', 'email': 'string', 'phoneNumber': 'string', 'remark': 'string'},
        searchFilterTrim || '*',);

      if (searchFilterTrim) {
        options.query['licensePlate'] = searchFilterTrim;
        options.query['addressNumber'] = searchFilterTrim;
      }

      if (searchRoleMember) {
        options.query['roleMemberName'] = searchRoleMember;
      }
    }

    if (searchDelete) {
      options.query['isShowDeleted'] = true;
    }

    if (!isNaN(options.limit)) {
      options.query['limit'] = options.limit;
    }

    let url = URLHelper.buildQueryString('/members/search?', options.query, '');
    if (searchCategory) {
      url = url + `;(status==${searchCategory})`;
    }

    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, url + '&sort=createdDate,desc');
  }

  extractThaiLicensePlateInfo(input: String): ParkingLotMemberQuery {
    const regex = /^([\u0E00-\u0E7F\d]+)\s?[-]?\s?(\d{4})(?:\s([\u0E00-\u0E7F\s]+))?$/;
    const matches = input.match(regex);
    const letter = matches[1] || '';
    const number = matches[2] || '';
    const province = matches[3] || '';

    return {
      licensePlateLetter: letter.trim(),
      licensePlateNumber: number.trim(),
      licensePlateProvince: province.trim()
    };
  }

  createParkingMember(memberBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.post('', {'parking-lots': 'PARKING_LOT_ID'}, '/members', memberBody, options);
  }

  updateParkingMember(membersId: number, memberBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.put('', {'parking-lots': 'PARKING_LOT_ID', 'members': membersId}, '', memberBody, options);
  }

  deleteParkingMembers(membersId: Array<any>): Promise<any> {
    const membersIdsFormat = membersId.map((data) => {
      return data.id;
    }).join(',');
    return this.delete('', {'parking-lots': 'PARKING_LOT_ID'}, '/members?ids=' + membersIdsFormat);
  }

  getParkingSubscribers(options: any, searchFilter: string, searchDelete: boolean, searchCategory?: any): Promise<any> {
    options = options || {};
    options.query = options.query || {};
    options.query['query'] = URLHelper.buildGeneralSearchFilter(
      {
        'name': 'string',
        'email': 'string',
        'phoneNumber': 'string',
        'licensePlateLetter': 'string',
        'licensePlateNumber': 'string',
        'licensePlateProvince': 'string',
        'remark': 'string'
      },
      searchFilter || '*');
    if (searchDelete) {
      options.query['isShowDeleted'] = true;
    }
    let url = URLHelper.buildQueryString('/subscribers/search?', options.query, '');
    if (searchCategory) {
      url = url + `;(status==${searchCategory})`;
    }
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, url + '&isShowMember=false&sort=createdDate,desc');
  }

  getParkingMemberCards(options: any, searchFilter: string, searchCategory?: any): Promise<any> {
    options = options || {};
    options.query = options.query || {};
    options.query['query'] = URLHelper.buildGeneralSearchFilter(
      {'cardRefId': 'string', 'cardRFId': 'string'},
      searchFilter || '*');
    let url = URLHelper.buildQueryString('/cards/search?', options.query, '');
    if (searchCategory) {
      url = url + `;(status==${searchCategory})`;
    }
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, url + '&sort=createdDate,desc');
  }

  getParkingMemberCardById(memberCardId): Promise<any> {
    const url = URLHelper.buildQueryString('/cards/' + memberCardId, '', '');
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, url);
  }

  createParkingSubscriber(subscriberBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.post('', {'parking-lots': 'PARKING_LOT_ID'}, '/subscribers', subscriberBody, options);
  }

  createParkingMemberCard(memberCardBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.post('', {'parking-lots': 'PARKING_LOT_ID'}, '/cards', memberCardBody, options);
  }

  updateParkingSubscribers(subscribersId: number, subscriberBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.put('', {'parking-lots': 'PARKING_LOT_ID', 'subscribers': subscribersId}, '', subscriberBody, options);
  }

  updateParkingMemberCard(memberCardId: number, memberCardBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.put('', {'parking-lots': 'PARKING_LOT_ID', 'cards': memberCardId}, '', memberCardBody, options);
  }

  deleteParkingSubscribers(subscribersId: Array<any>): Promise<any> {
    const subscribersIdsFormat = subscribersId.map((data) => {
      return data.id;
    }).join(',');
    return this.delete('', {'parking-lots': 'PARKING_LOT_ID'}, '/subscribers?ids=' + subscribersIdsFormat);
  }

  deleteParkingMemberCards(memberCardId: Array<any>): Promise<any> {
    const memberCardIdsFormat = memberCardId.map((data) => {
      return data.id;
    }).join(',');
    return this.delete('', {'parking-lots': 'PARKING_LOT_ID'}, '/cards?ids=' + memberCardIdsFormat);
  }

  getParkingUsers(options: any, searchFilter: string) {
    options = options || {};
    options.query = options.query || {};
    options.query['query'] = URLHelper.buildGeneralSearchFilter(
      {'name': 'string', 'email': 'string', 'phoneNumber': 'string'},
      searchFilter || '*');
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, URLHelper.buildQueryString('/users/search?', options.query, 'sort=createdDate,desc'));
  }

  getParkingUserRoles() {
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, '/roles');
  }

  createParkingUser(subscriberBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.post('', {'parking-lots': 'PARKING_LOT_ID'}, '/users', subscriberBody, options);
  }

  updateParkingUsers(userId: number, userBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.put('', {'parking-lots': 'PARKING_LOT_ID', 'users': userId}, '', userBody, options);
  }

  deleteParkingUsers(userId: Array<any>): Promise<any> {
    const usersIdsFormat = userId.map((data) => {
      return data.id;
    }).join(',');
    return this.delete('', {'parking-lots': 'PARKING_LOT_ID'}, '/users?ids=' + usersIdsFormat);
  }

  getRolePermissions(): Promise<any> {
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, '/rolePrivilege');
  }

  getParkingInventories(options: any, keyword?: string, inventoryType?: ParkingInventoryType, inventoryStatus?: ParkingInventoryStatus): Promise<any> {
    options = options || {};
    options.query = options.query || {};
    options.query['query'] = URLHelper.buildGeneralSearchFilter(
      {
        'name': 'string',
        'type': 'string',
        'location': 'string',
        'ipAddress': 'string'
      },
      keyword || '*');

    let url = URLHelper.buildQueryString('/inventories?', options.query, '');
    if (inventoryType) {
      url = url + `;(type==${inventoryType})`;
    }
    if (inventoryStatus) {
      url = url + `;(status==${inventoryStatus})`;
    }
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, url + '&sort=createdDate,desc');
  }

  // Discount
  getDiscounts(options: any, keyword?: string, discountAppliedFrom?: DiscountAppliedFrom): Promise<any> {
    options = options || {};
    options.query = options.query || {};
    options.query['query'] = URLHelper.buildGeneralSearchFilter(
      {
        'slipNumber': 'string',
        'licensePlateLetter': 'string',
        'licensePlateNumber': 'string',
        'licensePlateProvince': 'string',
      },
      keyword || '*');

    let url = URLHelper.buildQueryString('/discounts?', options.query, '');
    if (discountAppliedFrom) {
      url = url + `;(appliedFrom==${discountAppliedFrom})`;
    }
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, url + '&sort=createdDate,desc');
  }

  getCouponByCode(code: string): Promise<any> {
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, `/coupons/${code}`);
  }

  getParkingUsageById(parkingLotUsageId: number): Promise<ParkingLotUsage> {
    return this.get('', {'parking-lots': 'PARKING_LOT_ID', 'usages': parkingLotUsageId}, '');
  }

  // Virtual Card
  getSubscriberVirtualCard() {
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, '/virtual-cards');
  }

  getParkingBlacklists(options: any, searchFilter: string, searchCategory?: any): Promise<any> {
    options = options || {};
    options.query = options.query || {};
    options.query['query'] = URLHelper.buildGeneralSearchFilter(
      {
        'description': 'string',
        'licensePlateLetter': 'string',
        'licensePlateNumber': 'string',
        'licensePlateProvince': 'string'
      },
      searchFilter || '*');
    let url = URLHelper.buildQueryString('/blacklists/search?', options.query, '');
    if (searchCategory) {
      url = url + `;(type==${searchCategory})`;
    }
    return this.get('', {'parking-lots': 'PARKING_LOT_ID'}, url + '&sort=updatedDate,desc');
  }

  deleteParkingBlacklists(blacklistsIds: Array<any>): Promise<any> {
    const blacklistsIdsFormat = blacklistsIds.map((data) => {
      return data.id;
    }).join(',');
    return this.delete('', {'parking-lots': 'PARKING_LOT_ID'}, '/blacklists?ids=' + blacklistsIdsFormat);
  }

  requestBlacklistExcel(blacklistsIds: any, searchFilter: string): Promise<any> {
    const header = new HttpHeaders();
    header.append('Content-Type', 'application/json; charset=utf-8');
    header.append('mimeType', 'application/vnd.ms-excel');
    const options = {headers: header, responseType: 'blob', observe: 'response'};
    let url = URLHelper.buildQueryString('/blacklists/export-excel?', '', '');
    if (blacklistsIds) {
      url = url + '&query=id=' + ((blacklistsIds.length == 0) ? 'out=(-1)' : 'in=(' + blacklistsIds.join(',') + ')');
    } else {
      url = url + '&offset=0&limit=10000';
    }
    return this.post('reports/', {'parking-lots': 'PARKING_LOT_ID'}, url + '&sort=createdDate,desc', '', options);
  }

  importBlacklistExcel(fileInput): Promise<any> {
    if (fileInput.files.length == 0) {
      return new Promise((resolve, reject) => {
        reject(null);
      });
    }

    const formData: FormData = new FormData();
    formData.append('file', fileInput.files[0], fileInput.files[0].name);

    const header = new HttpHeaders();
    header.append('Content-Type', 'multipart/form-data');

    const options = {headers: header};
    return this.post('', {'parking-lots': 'PARKING_LOT_ID'}, '/blacklists/import-excel', formData, options);
  }

  updateParkingBlacklists(blacklistsIds: number, blacklistBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.put('', {'parking-lots': 'PARKING_LOT_ID', 'blacklists': blacklistsIds}, '', blacklistBody, options);
  }

  createParkingBlacklist(blacklistBody: any): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.post('', {'parking-lots': 'PARKING_LOT_ID'}, '/blacklists', blacklistBody, options);
  }

  resetBatterySpace(spaceId: number): Promise<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};
    return this.post('', {'parking-lots': 'PARKING_LOT_ID', 'spaces': spaceId}, '/reset-battery', null, options);
  }
}



// WEBPACK FOOTER //
// ./src/app/core/data-services/parking/parking.service.ts